import Client from "../models/serviceModels/Client";
import Program from "../models/serviceModels/Program";
import {
    HouseholdTrackingEligibility,
    HouseholdTrackingGroup,
    HouseholdTrackingSubmissionResult,
    HouseholdTrackingTarget
} from "../models/pageModels/HouseholdTracking";

const getDisplayName = (firstName?: string, middleName?: string, lastName?: string) =>
    [firstName, middleName, lastName].filter(value => !!value).join(' ').trim() || 'Unknown Person';

export const buildHouseholdTrackingGroups = (client: Client): HouseholdTrackingGroup[] => {
    const groups: HouseholdTrackingGroup[] = [];

    if ((client.householdMembers || []).length > 0) {
        groups.push({
            key: `own-${client.id}`,
            name: `${getDisplayName(client.firstName, client.middleName, client.lastName)} Household`,
            people: [
                {
                    key: `own-${client.id}-owner`,
                    clientId: client.id,
                    name: getDisplayName(client.firstName, client.middleName, client.lastName),
                    relationship: 'Head of Household',
                    isPrimary: true,
                    duplicateSubmission: false
                },
                ...client.householdMembers.map(member => ({
                    key: `own-${client.id}-member-${member.id}`,
                    clientId: member.linkedClientId,
                    name: getDisplayName(member.firstName, member.middleName, member.lastName),
                    relationship: member.relationshipName || 'Relationship not provided',
                    isPrimary: member.linkedClientId === client.id,
                    duplicateSubmission: false
                }))
            ]
        });
    }

    (client.linkedHouseholds || []).filter(household => household.members.length > 0).forEach(household => {
        const ownerName = getDisplayName(
            household.owner.firstName,
            household.owner.middleName,
            household.owner.lastName
        );

        groups.push({
            key: `linked-${household.owner.clientId}`,
            name: `${ownerName} Household`,
            people: [
                {
                    key: `linked-${household.owner.clientId}-owner`,
                    clientId: household.owner.clientId,
                    name: ownerName,
                    relationship: 'Head of Household',
                    isPrimary: household.owner.clientId === client.id,
                    duplicateSubmission: false
                },
                ...household.members.map(member => ({
                    key: `linked-${household.owner.clientId}-member-${member.id}`,
                    clientId: member.linkedClientId,
                    name: getDisplayName(member.firstName, member.middleName, member.lastName),
                    relationship: member.relationshipName || 'Relationship not provided',
                    isPrimary: member.linkedClientId === client.id,
                    duplicateSubmission: false
                }))
            ]
        });
    });

    const seenClientIds = new Set<string>();
    return groups.map(group => ({
        ...group,
        people: group.people.map(person => {
            if (!person.clientId) {
                return person;
            }

            const duplicateSubmission = seenClientIds.has(person.clientId);
            seenClientIds.add(person.clientId);
            return {
                ...person,
                duplicateSubmission
            };
        })
    }));
};

export const buildHouseholdTrackingTargets = (
    client: Client,
    groups: HouseholdTrackingGroup[]
): HouseholdTrackingTarget[] => {
    const targets = new Map<string, HouseholdTrackingTarget>();
    targets.set(client.id, {
        clientId: client.id,
        name: getDisplayName(client.firstName, client.middleName, client.lastName),
        householdRelationships: [],
        isPrimary: true
    });

    groups.forEach(group => {
        group.people.forEach(person => {
            if (!person.clientId) {
                return;
            }

            const relationship = `${group.name}: ${person.relationship}`;
            const existing = targets.get(person.clientId);
            if (existing) {
                if (!existing.householdRelationships.includes(relationship)) {
                    existing.householdRelationships.push(relationship);
                }
                return;
            }

            targets.set(person.clientId, {
                clientId: person.clientId,
                name: person.name,
                householdRelationships: [relationship],
                isPrimary: person.clientId === client.id
            });
        });
    });

    return Array.from(targets.values());
};

export const getHouseholdTrackingReviewedPeopleCount = (
    client: Client,
    groups: HouseholdTrackingGroup[]
) => {
    const people = new Set<string>();
    people.add(client.id);
    groups.forEach(group => {
        group.people.forEach(person => people.add(person.clientId || person.key));
    });
    return people.size;
};

export const getHouseholdTrackingIneligiblePeopleCount = (
    groups: HouseholdTrackingGroup[],
    eligibility: Record<string, HouseholdTrackingEligibility>
) => {
    const people = new Set<string>();
    groups.forEach(group => {
        group.people.forEach(person => {
            if (!person.clientId || eligibility[person.clientId]?.status === 'ineligible') {
                people.add(person.clientId || person.key);
            }
        });
    });
    return people.size;
};

export const resolveHouseholdTrackingEligibility = async (
    targets: HouseholdTrackingTarget[],
    primaryClientId: string,
    program: Program,
    getPrograms: (clientId: string) => Promise<Program[]>
): Promise<Record<string, HouseholdTrackingEligibility>> => {
    const eligibility: Record<string, HouseholdTrackingEligibility> = {
        [primaryClientId]: {
            status: 'eligible'
        }
    };

    await Promise.all(targets.filter(target => target.clientId !== primaryClientId).map(async target => {
        try {
            const programs = await getPrograms(target.clientId);
            eligibility[target.clientId] = programs.some(enrolledProgram => enrolledProgram.id === program.id)
                ? { status: 'eligible' }
                : { status: 'ineligible', reason: `Not enrolled in ${program.name}` };
        } catch (error) {
            eligibility[target.clientId] = {
                status: 'ineligible',
                reason: 'Could not verify program enrollment'
            };
        }
    }));

    return eligibility;
};

export const getSelectableHouseholdTrackingClientIds = (
    targets: HouseholdTrackingTarget[],
    eligibility: Record<string, HouseholdTrackingEligibility>
) => targets
    .filter(target => eligibility[target.clientId]?.status === 'eligible')
    .map(target => target.clientId);

export interface RetryOptions {
    retries?: number;
    baseDelayMs?: number;
    wait?: (delayMs: number) => Promise<void>;
    onAttempt?: (attempt: number) => void;
    onRetry?: (nextAttempt: number, delayMs: number, error: unknown) => void;
}

class RetryExecutionError extends Error {
    attempts: number;
    originalError: unknown;

    constructor(originalError: unknown, attempts: number) {
        super(originalError instanceof Error ? originalError.message : 'Operation failed');
        this.name = 'RetryExecutionError';
        this.attempts = attempts;
        this.originalError = originalError;
    }
}

const defaultWait = (delayMs: number) => new Promise<void>(resolve => {
    window.setTimeout(resolve, delayMs);
});

export const executeWithRetry = async <T>(
    operation: () => Promise<T>,
    options: RetryOptions = {}
): Promise<{ value: T, attempts: number }> => {
    const retries = options.retries ?? 3;
    const baseDelayMs = options.baseDelayMs ?? 1000;
    const wait = options.wait || defaultWait;

    for (let attempt = 1; attempt <= retries + 1; attempt += 1) {
        options.onAttempt?.(attempt);
        try {
            return {
                value: await operation(),
                attempts: attempt
            };
        } catch (error) {
            if (attempt > retries) {
                throw new RetryExecutionError(error, attempt);
            }

            const delayMs = baseDelayMs * Math.pow(2, attempt - 1);
            options.onRetry?.(attempt + 1, delayMs, error);
            await wait(delayMs);
        }
    }

    throw new Error('Retry operation ended unexpectedly');
};

const getErrorMessage = (error: any) =>
    error?.originalError?.response?.data?.message
    || error?.originalError?.message
    || error?.response?.data?.message
    || error?.message
    || 'Failed to submit form';

export interface RunSubmissionOptions {
    concurrency?: number;
    retries?: number;
    baseDelayMs?: number;
    wait?: (delayMs: number) => Promise<void>;
    onJobUpdate?: (target: HouseholdTrackingTarget, status: 'submitting' | 'retrying', attempt: number) => void;
    onJobComplete?: (result: HouseholdTrackingSubmissionResult) => void;
}

export const runHouseholdTrackingSubmissions = async (
    targets: HouseholdTrackingTarget[],
    submit: (target: HouseholdTrackingTarget) => Promise<unknown>,
    options: RunSubmissionOptions = {}
): Promise<HouseholdTrackingSubmissionResult[]> => {
    const concurrency = Math.max(1, options.concurrency ?? 3);
    const results: HouseholdTrackingSubmissionResult[] = [];
    let nextIndex = 0;

    const worker = async () => {
        while (nextIndex < targets.length) {
            const target = targets[nextIndex];
            nextIndex += 1;

            try {
                const response = await executeWithRetry(
                    () => submit(target),
                    {
                        retries: options.retries,
                        baseDelayMs: options.baseDelayMs,
                        wait: options.wait,
                        onAttempt: attempt => options.onJobUpdate?.(target, attempt === 1 ? 'submitting' : 'retrying', attempt),
                        onRetry: nextAttempt => options.onJobUpdate?.(target, 'retrying', nextAttempt)
                    }
                );
                const result: HouseholdTrackingSubmissionResult = {
                    ...target,
                    status: 'succeeded',
                    attempts: response.attempts
                };
                results.push(result);
                options.onJobComplete?.(result);
            } catch (error: any) {
                const result: HouseholdTrackingSubmissionResult = {
                    ...target,
                    status: 'failed',
                    attempts: error?.attempts || (options.retries ?? 3) + 1,
                    error: getErrorMessage(error)
                };
                results.push(result);
                options.onJobComplete?.(result);
            }
        }
    };

    await Promise.all(Array.from(
        { length: Math.min(concurrency, targets.length) },
        () => worker()
    ));

    return targets.map(target => results.find(result => result.clientId === target.clientId)!);
};
